/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */
import { call, put, select, takeLatest } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import uuidv4 from 'uuid/v4';
import moment from 'moment';
import { operatorTyping, setNotification } from 'utils';
import { omit, uniq } from 'lodash';
import { getOrderMacro, getUsersMacro } from './macros';
import * as AccountancyActions from '../actions/accountancyActions';
import * as AccountancyApi from '../api/accountancyApi';
import { getOrderApi } from '../api/orderApi';
import { downloadAllInvoicesApi, getDownloadAllInvoicesApi } from '../api/customerApi';
import * as AccountancyTypes from '../types/accountancyTypes';

const getLayout = state => state.layout;
const getAccountancy = state => state.accountancy;

const exportTypes = {
    printconcept: 'VERO_EXPORT',
    mercuryflooring: 'MF_INAC_EXPORT',
};


// E X P O R T S
function* loadAccountancyExportsSaga() {
    const { admin } = yield select(getLayout);
    const { exportsListing: { offset, limit } } = yield select(getAccountancy);

    try {
        const result = yield AccountancyApi.getExportInvoicesApi({
            filter: JSON.stringify({
                type: exportTypes[admin],
            }),
            offset,
            limit,
            sort_by: JSON.stringify({ created_at: -1 }),
        });

        yield put(AccountancyActions.exportAccountancyGroupsRes(result.data));
    } catch (e) {
        yield put(AccountancyActions.exportAccountancyGroupsErr());
        console.log(e);
    }
}

export function* loadAccountancyExportsWatcher() {
    yield takeLatest(AccountancyTypes.LOAD_ACCOUNTANCY_EXPORTS, loadAccountancyExportsSaga);
}

function* ExportAccountancyGroupsSaga(action) {
    const { admin } = yield select(getLayout);

    try {
        const filters = omit(action.data, ['created_at']);

        if (action.data.created_at.length > 0) {
            const [firstDate, lastDate] = action.data.created_at;
            filters.created_at = { between: [moment(firstDate).format('YYYY-MM-DD 00:00:00'), moment(lastDate).format('YYYY-MM-DD 23:59:59')] };
        }

        const params = {
            export_id: uuidv4(),
            type: exportTypes[admin],
            sort: JSON.stringify({}),
            filter: JSON.stringify(filters),
        };

        const result = yield AccountancyApi.exportAccountancyGroupsApi(params);

        if (result.status === 202) {
            yield put(AccountancyActions.setAccountancyExportsPagination('offset', 0));
            yield put(AccountancyActions.setAccountancyExportsPagination('limit', 50));
            yield call(loadAccountancyExportsSaga, {});
        } else {
            yield put(AccountancyActions.exportAccountancyGroupsErr());
        }
    } catch (e) {
        yield put(AccountancyActions.exportAccountancyGroupsErr());
        console.log(e);
    }
}

export function* ExportAccountancyGroupsWatcher() {
    yield takeLatest(AccountancyTypes.EXPORT_ACCOUNTANCY_GROUPS, ExportAccountancyGroupsSaga);
}
// E X P O R T S


// A C C O U N T A N C Y   G R O U P S
function* loadAccountancyGroupsSaga() {
    try {
        const result = yield AccountancyApi.getAccountancyGroupsApi({
            offset: 0,
            limit: 50,
        });

        yield put(AccountancyActions.loadAccountancyGroupsRes(result.data));
    } catch (e) {
        yield put(AccountancyActions.loadAccountancyGroupsErr());
        console.log(e);
    }
}

export function* loadAccountancyGroupsWatcher() {
    yield takeLatest(AccountancyTypes.LOAD_ACCOUNTANCY_GROUPS, loadAccountancyGroupsSaga);
}
// A C C O U N T A N C Y   G R O U P S

// I N V O I C E
function* loadAccountancyInvoiceSaga() {
    const { invoice: { offset, limit, filters } } = yield select(getAccountancy);

    try {
        const filterParams = {
            invoice_type: filters.isProforma ? 'PROFORMA' : ['INVOICE', 'CREDIT_NOTE'],
        };

        if (filters.paymentStatus !== undefined) {
            filterParams.payment_status = filters.paymentStatus;
        }

        if (filters.invoiceNumber !== '') {
            filterParams['invoice_number.invoice_number_alias'] = operatorTyping(filters.invoiceNumber.trim());
        }

        if (filters.billingAccount !== '') {
            filterParams.invoice_account_number = operatorTyping(filters.billingAccount.trim());
        }

        if (filters.customerNumber !== '') {
            filterParams.customer_number = operatorTyping(filters.customerNumber.trim());
        }

        if (filters.orderNumber !== '') {
            const resultOrders = yield getOrderApi({ filter: JSON.stringify({ order_number: filters.orderNumber }) });
            const [ordersId] = resultOrders.data.hits;
            filterParams.orders_id = ordersId._id;
        }

        if (filters.company !== '') {
            filterParams.invoice_account_company = operatorTyping(filters.company.trim());
        }

        if (filters.clientName !== '') {
            filterParams.invoice_account_name = operatorTyping(filters.clientName.trim());
        }

        if (filters.timeLimit !== '') {
            filterParams.limit_days = parseInt(filters.timeLimit.trim(), 10);
        }

        if (filters.ogm !== '') {
            filterParams.ogm = operatorTyping(filters.ogm.trim());
        }

        if (filters.vatNumber !== '') {
            filterParams.invoice_account_vat_number = operatorTyping(filters.vatNumber.trim());
        }

        if (filters.source !== '') {
            filterParams.source = filters.source;
        }

        if (filters.createdAt.length > 0) {
            const customDate = [
                moment.utc(filters.createdAt[0]).format('YYYY-MM-DD 00:00:00'),
                moment.utc(filters.createdAt[1]).format('YYYY-MM-DD 23:59:59'),
            ];
            filterParams.created_at = { between: customDate };
        }

        if (filters.daysOpen !== '') {
            const customDate = [
                moment().add(parseInt(filters.daysOpen.trim(), 10), 'days').format('YYYY-MM-DD 00:00:00'),
                moment().add(parseInt(filters.daysOpen.trim(), 10), 'days').format('YYYY-MM-DD 23:59:59'),
            ];
            filterParams.expiration_date = { between: customDate };
        }

        const result = yield AccountancyApi.getAccountancyInvoicesApi({
            offset,
            limit,
            sort_by: JSON.stringify({ created_at: -1 }),
            filter: JSON.stringify(filterParams),
        });

        yield put(AccountancyActions.loadAccountancyInvoiceRes(result.data));

        if (result.data.hits?.length > 0) {
            const orderIDs = [];
            const userIDs = [];

            result.data.hits.forEach((res) => {
                if (res.order_id) {
                    orderIDs.push(res.order_id);
                }
            });

            if (orderIDs.length > 0) {
                yield put(AccountancyActions.loadAccountancyInvoiceOrders());
                const orderResult = yield getOrderMacro(uniq(orderIDs));

                if (orderResult?.data?.hits.length > 0) {
                    orderResult.data.hits.forEach((order) => {
                        if (order.sales_man_id) {
                            userIDs.push(order.sales_man_id);
                        }
                    });

                    if (userIDs.length > 0) {
                        const userResult = yield getUsersMacro(uniq(userIDs));

                        if (userResult.data?.hits.length > 0) {
                            const orders = orderResult.data.hits.map((order) => {
                                const relatedUser = userResult.data.hits.find(u => u._id === order.sales_man_id);
                                return {
                                    ...order,
                                    managerName: relatedUser ? `${relatedUser.first_name} ${relatedUser.last_name}` : null,
                                };
                            });
                            yield put(AccountancyActions.loadAccountancyInvoiceOrdersRes(orders));
                        } else {
                            yield put(AccountancyActions.loadAccountancyInvoiceOrdersErr());
                        }
                    } else {
                        yield put(AccountancyActions.loadAccountancyInvoiceOrdersErr());
                    }
                } else {
                    yield put(AccountancyActions.loadAccountancyInvoiceOrdersErr());
                }
            }
        }
    } catch (e) {
        yield put(AccountancyActions.loadAccountancyInvoiceErr());
        console.log(e);
    }
}

export function* loadAccountancyInvoiceWatcher() {
    yield takeLatest(AccountancyTypes.LOAD_ACCOUNTANCY_INVOICE, loadAccountancyInvoiceSaga);
}
// I N V O I C E

// E X P O R T   I N V O I C E S
function* checkInvoicesFile(exportId, action, count = 0) {
    const paramsDownload = {
        filter: {
            id: exportId,
        },
    };
    paramsDownload.filter = JSON.stringify(paramsDownload.filter);
    const resultDownload = yield getDownloadAllInvoicesApi(paramsDownload);
    if (resultDownload.status === 200) {
        const [invoicesFile] = resultDownload.data.hits;

        if (invoicesFile?.file !== null) {
            yield put(AccountancyActions.exportInvoicesRes());
            yield delay(1000);

            const link = document.createElement('a');
            link.href = invoicesFile.file;
            link.download = true;
            link.click();
        } else if (invoicesFile?.status === 'CREATED' && count < 5) {
            const recount = count + 1;
            yield delay(3000);
            yield checkInvoicesFile(exportId, action, recount);
        } else {
            yield put(AccountancyActions.exportInvoicesErr());
        }
    }
}

function* exportInvoicesSaga(action) {
    const { locale } = yield select(getLayout);
    const { invoice: { filters } } = yield select(getAccountancy);

    try {
        const params = {
            export_id: uuidv4(),
            type: action.invoiceAction === 'download' ? 'INVOICE_PDF' : 'INVOICE_CSV',
            filter: {},
            sort: [],
        };

        const filterParams = {
            invoice_type: action.invoiceType === 'invoices' ? 'INVOICE' : 'PROFORMA',
        };

        if (filters.paymentStatus !== undefined) {
            filterParams.payment_status = filters.paymentStatus;
        }

        if (filters.invoiceNumber !== '') {
            filterParams['invoice_number.invoice_number_alias'] = operatorTyping(filters.invoiceNumber.trim());
        }

        if (filters.billingAccount !== '') {
            filterParams.invoice_account_number = operatorTyping(filters.billingAccount.trim());
        }

        if (filters.customerNumber !== '') {
            filterParams.customer_number = operatorTyping(filters.customerNumber.trim());
        }
        if (filters.orderNumber !== '') {
            filterParams.order_number = operatorTyping(filters.orderNumber.trim());
        }

        if (filters.company !== '') {
            filterParams.invoice_account_company = operatorTyping(filters.company.trim());
        }

        if (filters.clientName !== '') {
            filterParams.invoice_account_name = operatorTyping(filters.clientName.trim());
        }

        if (filters.timeLimit !== '') {
            filterParams.limit_days = parseInt(filters.timeLimit.trim(), 10);
        }
        if (filters.ogm !== '') {
            filterParams.ogm = operatorTyping(filters.ogm.trim());
        }

        if (filters.vatNumber !== '') {
            filterParams.invoice_account_vat_number = operatorTyping(filters.vatNumber.trim());
        }

        if (filters.createdAt.length > 0) {
            const customDate = [
                moment.utc(filters.createdAt[0]).format('YYYY-MM-DD 00:00:00'),
                moment.utc(filters.createdAt[1]).format('YYYY-MM-DD 23:59:59'),
            ];
            filterParams.created_at = { between: customDate };
        }

        if (filters.daysOpen !== '') {
            const customDate = [
                moment().add(parseInt(filters.daysOpen.trim(), 10), 'days').format('YYYY-MM-DD 00:00:00'),
                moment().add(parseInt(filters.daysOpen.trim(), 10), 'days').format('YYYY-MM-DD 23:59:59'),
            ];
            filterParams.expiration_date = { between: customDate };
        }

        params.filter = JSON.stringify(filterParams);
        params.limit = action.limit;
        params.sort = JSON.stringify(params.sort);

        const result = yield downloadAllInvoicesApi(params);
        if (result.status === 202) {
            yield delay(2000);
            yield checkInvoicesFile(params.export_id, action);
        } else {
            const error = yield result.json();

            setNotification('error', locale, 'global.error', error.message, { noTranslateMsg: true });
            yield put(AccountancyActions.exportInvoicesErr());
        }
    } catch (e) {
        yield put(AccountancyActions.exportInvoicesErr());
        console.log(e);
    }
}

export function* exportInvoicesWatcher() {
    yield takeLatest(AccountancyTypes.EXPORT_INVOICES, exportInvoicesSaga);
}
// E X P O R T   I N V O I C E S
